import { ReactElement, useState, useCallback } from 'react';

import { Timestamp } from 'firebase/firestore';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import DeleteIcon from '@mui/icons-material/Delete';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import { useData } from '../../contextProviders/DataProvider';
import Season from '../../types/Season';
import { FromDayJS, ToDayJS } from '../../utils/utils';

interface SeasonEditorProps {
  season: Season;
  onChange?: (value: Partial<Season>) => void;
  onDelete?: (value: Season) => void;
}

export default function SeasonEditor(props: SeasonEditorProps): ReactElement {
  const { season, onChange, onDelete } = props;
  const { isLeagueAdmin, isOrgAdmin } = useData();
  const [deleteConfirm, setDeleteConfirm] = useState<string>('');
  const [expanded, setExpanded] = useState(false);

  const handleSeasonChange = useCallback(
    (changes: Partial<Season>) => {
      if (onChange) {
        onChange({ ...changes, itemId: season.itemId, orgId: season.orgId, leagueId: season.leagueId });
      }
    },
    [onChange, season]
  );

  const handleSeasonDelete = () => {
    if (onDelete) {
      onDelete(season);
      setDeleteConfirm('');
    }
  };

  if (season && (isOrgAdmin(season.orgId) || isLeagueAdmin(season.leagueId, season.orgId))) {
    return (
      <Stack spacing={1}>
        {/* Main row */}
        <Stack direction="row" spacing={1} sx={{
          alignItems: "center"
        }}>
          <TextField
            label="Name"
            variant="outlined"
            size="small"
            value={season.name}
            sx={{ width: { xs: '10ch', sm: '20ch' } }}
            onChange={(e) => handleSeasonChange({ name: e.target.value })}
          />
          <DatePicker
            label="Start"
            aria-label="start date"
            aria-required="true"
            format="MM/DD/YY"
            value={season.startTime instanceof Timestamp ? ToDayJS(season.startTime) : null}
            slotProps={{ textField: { size: 'small' } }}
            sx={{ width: { xs: '7.5rem', sm: '9.5rem' } }}
            onAccept={(dayJS) => {
              handleSeasonChange({
                startTime: FromDayJS(dayJS),
                endTime: FromDayJS(dayJS) > season.endTime ? FromDayJS(dayJS) : season.endTime,
              });
            }}
          />
          <DatePicker
            label="End"
            aria-label="End date"
            aria-required="true"
            format="MM/DD/YY"
            value={season.endTime instanceof Timestamp ? ToDayJS(season.endTime) : null}
            minDate={season.startTime instanceof Timestamp ? ToDayJS(season.startTime) : undefined}
            slotProps={{ textField: { size: 'small' } }}
            sx={{ width: { xs: '7.5rem', sm: '9.5rem' } }}
            onAccept={(dayJS) => {
              handleSeasonChange({ endTime: FromDayJS(dayJS) });
            }}
          />
          <IconButton size="small" onClick={() => setExpanded((v) => !v)} title={expanded ? 'Hide options' : 'More options'}>
            {expanded ? <ExpandLessIcon /> : <MoreVertIcon />}
          </IconButton>
        </Stack>
        {/* Expanded row: GGID, GG Portal, Delete */}
        {expanded && (
          <Stack direction="row" spacing={1} sx={{
            alignItems: "center"
          }}>
            {deleteConfirm === season.itemId ? (
              <>
                <Button variant="outlined" size="small" color="error" onClick={handleSeasonDelete}>
                  Confirm delete
                </Button>
                <Button size="small" color="secondary" onClick={() => setDeleteConfirm('')}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <TextField
                  label="GGID"
                  variant="outlined"
                  size="small"
                  value={season.ggid || ''}
                  sx={{ width: { xs: '10ch', sm: '12ch'} }}
                  slotProps={{ htmlInput: { maxLength: 6 } }}
                  onChange={(e) => handleSeasonChange({ ggid: e.target.value })}
                />
                <TextField
                  label="GG Portal"
                  variant="outlined"
                  size="small"
                  value={season.ggPortal || ''}
                  sx={{ width: { xs: '25ch', sm: '39ch'} }}
                  slotProps={{ htmlInput: { maxLength: 50 } }}
                  onChange={(e) => handleSeasonChange({ ggPortal: e.target.value })}
                />
                <IconButton size="small" aria-label="delete" onClick={() => setDeleteConfirm(season.itemId)}>
                  <DeleteIcon />
                </IconButton>
              </>
            )}
          </Stack>
        )}
      </Stack>
    );
  } else {
    return <></>;
  }
}
